/**
 * Custom web component that provides a configurable share button with a custom dialog.
 * Displays a modal dialog with selectable share targets including email, WhatsApp, SMS, and clipboard copy.
 * Uses native protocol handlers for sharing.
 * All configuration is done via HTML attributes.
 * @class ShareButton
 * @extends WebComponent
 * @example
 * // Basic usage
 * <share-button
 *     id="my-share-btn"
 *     class="primary-button"
 *     label="Share this"
 *     icon-classname="icon-share"
 *     share-url="https://example.com"
 *     share-text="Check this out">
 * </share-button>
 * @example
 * @example
 * // Programmatic sharing
 * const button = document.querySelector('share-button');
 * await button.share({ url: 'https://example.com', title: 'Title', text: 'Text' });
 * @remarks
 * Configuration is entirely attribute-based. Supported attributes:
 * - class: Applied to the internal button element, then removed from host after synchronization.
 * - label: Button label text (visible); no default is applied.
 * - icon-classname: Icon class name(s) applied to the icon span (optional).
 * - share-url: The URL to share (defaults to current page URL if omitted).
 * - share-text: Text to include in the share message (defaults to document.title).
 * - disabled: Whether the button is disabled (presence of attribute disables button).
 * - locale: Language code for localization (defaults to browser locale).
 *
 * Supported targets:
 * - copy: Copies URL to clipboard
 * - email: Opens native email client via mailto: protocol.
 * - sms: Opens SMS app via sms: protocol.
 * - whatsapp: Opens WhatsApp app via whatsapp: protocol.
 *
 * Protocol handler behavior:
 * - Uses native protocol handlers to open external applications.
 *
 * Dialog features:
 * - Modal overlay.
 * - ARIA-compliant with proper labels and roles.
 * - Mobile-first responsive design.
 *
 * @memberof WebComponents
 */
class ShareButton extends WebComponent {

    // #region: Private Fields *******************************************************************/
    /**
     * AbortController for event listener cleanup.
     * @type {AbortController|null}
     * 
     * @private
     * @memberof ShareButton
     */
    #abortController;

    /**
     * The button element.
     * @type {HTMLButtonElement|null}
     * 
     * @private
     * @memberof ShareButton
     */
    #button;

    /**
     * The dialog-window element.
     * @type {HTMLElement|null}
     * 
     * @private
     * @memberof ShareButton
     */
    #dialog;

    /**
     * Flag to prevent reentrancy during share operations.
     * @type {boolean}
     * 
     * @private
     * @memberof ShareButton
     */
    #isSharing;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     * @private
     * @memberof DateTimePicker
     */
    #locales;

    /**
     * The current share data.
     * @type {Object|null}
     * 
     * @private
     * @memberof ShareButton
     */
    #shareData;

    /**
     * The available share targets after filtering.
     * @type {string[]|null}
     * 
     * @private
     * @memberof ShareButton
     */
    #availableTargets;

    /**
     * Element that had focus before dialog opened.
     * @type {Element|null}
     * 
     * @private
     * @memberof ShareButton
     */
    #previouslyFocused;
    // #endregion

    // #region: Constructor **********************************************************************/
    /**
     * Creates a new ShareButton instance.
     * 
     * @memberof ShareButton
     */
    constructor() {
        super();

        this.#locales = this.registerLocaleNamespace();

        this.#abortController = null;
        this.#availableTargets = null;
        this.#button = null;
        this.#dialog = null;
        this.#isSharing = false;
        this.#previouslyFocused = null;
        this.#shareData = null;
    }
    // #endregion

    // #region: Build ****************************************************************************/
    /**
     * @inheritdoc
     */
    static get observedAttributes() {
        return ['aria-label', 'class', 'disabled', 'icon-classname', 'label', 'locale', 'title'];
    }

    /**
     * Registered tag name for this web component.
     * @type {string}
     * 
     * @static
     * @memberof ShareButton
     */
    static tagName = 'share-button';

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof ShareButton
     */
    static toString() {
        return '[ShareButton]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof ShareButton
     */
    static version() {
        return '1.0.0';
    }

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     * 
     * @memberof ShareButton
     */
    toString() {
        return '[ShareButton]';
    }
    // #endregion

    // #region: Lifecycle Methods ****************************************************************/
    /**
     * @inheritdoc
     */
    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        
        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'aria-label':
                this.#setButtonAriaLabel();

                break;

            case 'title':
                this.#setButtonAriaLabel();

                break;

            case 'class':
            case 'icon-classname':
            case 'label':
            case 'locale':
                this.#updateButtonContent();

                break;

            case 'disabled':
                this.#updateDisabledState();

                break;

            default:
                break;
        
        }
    }

    /**
     * @inheritdoc
     */
    connectedCallback() {
        super.connectedCallback();

        this.#render();
    }

    /**
     * @inheritdoc
     */
    disconnectedCallback() {
        this.#shareData = null;

        if (this.#abortController) {
            this.#abortController.abort();
            this.#abortController = null;
        }

        this.#cleanup();
        super.disconnectedCallback();
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/

    /**
     * Handles dialog close events.
     *
     * @memberof ShareButton
     */
    #onDialogClose = () => {
        this.#abortController?.abort();
        this.#abortController = null;
        this.#availableTargets = null;
        this.#shareData = null;

        if (coreUI.isInteractive(this.#previouslyFocused)) {
            this.#previouslyFocused.focus();
        }

        this.#previouslyFocused = null;
    };

    /**
     * Updates cached locales when the active language changes.
     * @param {string} oldLocale The previous locale.
     * @param {string} newLocale The new locale.
     *
     * @memberof ShareButton
     */
    onLocaleChanged(oldLocale, newLocale) {
        super.onLocaleChanged?.(oldLocale, newLocale);

        this.#updateButtonContent();
    }

    /**
     * Handles target button click events.
     * @param {string} target Target name.
     * 
     * @private
     * @memberof ShareButton
     */
    #onTargetClick = (target) => {
        return this.#handleClickTarget(target);
    };
    // #endregion

    // #region: Public Methods *******************************************************************/
    /**
     * Opens native share or dialog using stored share data.
     * @param {Object} [shareData] Share data object.
     * @param {string} [shareData.text] Text description of the content.
     * @param {string} [shareData.title] Title of the content.
     * @param {string} [shareData.url] URL to share.
     * @returns {Promise<void>} A promise that resolves when sharing is initiated.
     * @remarks Stores share data in a private field until dialog closes or sharing completes.
     *
     * @memberof ShareButton
     */
    async share(shareData = {}) {
        if (this.#isSharing || this.#shareData !== null) {
            return;
        }

        this.#isSharing = true;

        const data = { ...shareData };
        data.url = new URL(data.url || window.location.href, window.location.href).toString();

        this.#shareData = Object.freeze(data);

        await this.#openDialog();

        this.#isSharing = false;

        if (this.#button) {
            this.#button.disabled = this.hasAttribute('disabled');
        }
    }
    // #endregion

    // #region: Private Methods *****************************************************************/
    /**
     * Builds content combining text and url with a given separator.
     * @param {string} separator Separator to use between parts.
     * @returns {string} Formatted content.
     * 
     * @private
     * @memberof ShareButton
     */
    #buildShareContent(separator) {
        let content = '';
        if (Array.isArray(this.#shareData?.text)) {
            content = this.#shareData.text.join(separator);
        }

        if (this.#shareData?.url) {
            content += (content.length > 0 ? separator + separator : '') + this.#shareData?.url;
        }

        return content;
    }

    /**
     * Cleans up resources.
     * 
     * @private
     * @memberof ShareButton
     */
    #cleanup() {
        if (this.#abortController) {
            this.#abortController.abort();
            this.#abortController = null;
        }

        if (this.#dialog) {
            this.#dialog.remove();
            this.#dialog = null;
        }

        if (this.#button) {
            this.#button.remove();
            this.#button = null;
        }

        this.#isSharing = false;
        this.#shareData = null;
    }

    /**
     * Closes the share dialog.
     * 
     * @private
     * @memberof ShareButton
     */
    #closeDialog() {
        if (!this.#dialog) {
            return;
        }

        this.#dialog.close();
    }

    /**
     * Copies text to clipboard with fallback for insecure contexts.
     * @param {string} text Text to copy.
     * @returns {Promise<boolean>} A promise that resolves to true if copy succeeded.
     * 
     * @private
     * @memberof ShareButton
     */
    async #copyToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
            try {
                await navigator.clipboard.writeText(text);

                return true;
            }
            catch {
                // Ignore.
            }
        }

        // Try 'textarea' fall back (Clipboard API might be disabled).
        const textarea = document.createElement('textarea');

        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.setAttribute('aria-hidden', 'true');
        textarea.tabIndex = -1;

        document.body.appendChild(textarea);
        textarea.setAttribute('readonly', '');
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);

        let success = false;

        try {
            success = document.execCommand('copy');
        }
        catch {
        }

        document.body.removeChild(textarea);

        return success;
    }

    /**
     * Creates the dialog element.
     * 
     * @private
     * @memberof ShareButton
     */
    #createDialog() {
        const shareLabel = this.#locales?.share || 'Delen';

        this.#dialog = DialogWindow.create({
            autoSize: true,
            headless: false,
            icon: this.getAttribute('icon-classname') || '',
            modal: true,
            owner: 'top',
            title: shareLabel
        });
        
        this.#dialog.className = 'share-dialog';
        this.#dialog.addEventListener('dialogclose', this.#onDialogClose);
    }

    /**
     * Creates a target button.
     * @param {string} target Target name.
     * @returns {HTMLButtonElement} Target button element.
     * 
     * @private
     * @memberof ShareButton
     */
    #createTargetButton(target) {
        const button = this.#dialog?.ownerDocument?.createElement('button') || document.createElement('button');
        const icon = this.#dialog?.ownerDocument?.createElement('span') || document.createElement('span');
        const label = this.#locales[`provider_${target}`] || target;
        const labelSpan = this.#dialog?.ownerDocument?.createElement('span') || document.createElement('span');
        const ariaLabel = this.#locales[`share_via_${target}`] || label;

        button.className = 'share-target-button';
        button.type = 'button';
        button.setAttribute('aria-label', ariaLabel);

        icon.className = `share-target-icon share-target-icon-${target}`;
        icon.setAttribute('aria-hidden', 'true');

        labelSpan.className = 'share-target-label';
        labelSpan.textContent = label ?? '';

        button.appendChild(icon);
        button.appendChild(labelSpan);

        const options = this.#abortController ? { signal: this.#abortController.signal } : undefined;

        button.addEventListener('click', () => {
            this.#onTargetClick(target);
        }, options);

        return button;
    }

    /**
     * Gets the available share targets.
     * Returns hardcoded list of supported targets excluding SMS on non-mobile devices.
     * @returns {string[]} Array of available target names.
     * 
     * @private
     * @memberof ShareButton
     */
    #getAvailableTargets() {
        const baseTargets = ['copy', 'email', 'whatsapp'];

        if (coreUI.isMobile) {
            baseTargets.push('sms');
        }

        return baseTargets;
    }

    /**
     * Handles target button click.
     * @param {string} target Target name.
     * 
     * @private
     * @memberof ShareButton
     */
    async #handleClickTarget(target) {
        if (this.#isSharing) {
            return;
        }

        this.#lockDialog();

        try {
            switch (target.toLowerCase()) {

                case 'copy':
                    await this.#handleCopyTarget();

                    break;

                case 'email':
                    this.#handleEmailTarget();

                    break;

                case 'sms':
                    this.#handleSmsTarget();

                    break;

                case 'whatsapp':
                    this.#handleWhatsAppTarget();

                    break;

                default:
                    // Ignore.
                    break;
            
            }
        }
        finally {
            this.#unlockDialog();
        }
    }
    
    /**
     * Handles copy target.
     * 
     * @private
     * @memberof ShareButton
     */
    async #handleCopyTarget() {
        const success = await this.#copyToClipboard(this.#shareData?.url || '');

        if (!success) {
            core.notify('error', this.#locales?.copyFailed || 'Kopiëren mislukt.');

            return;
        }

        this.#closeDialog();
        core.notify('success', this.#locales?.copySuccess || 'Link gekopieerd naar klembord');
    }

    /**
     * Handles email target.
     * 
     * @private
     * @memberof ShareButton
     */
    #handleEmailTarget() {
        const MAX_MAILTO_LENGTH = 1800;
        const messageContent = this.#buildShareContent('\r\n');
        const message = messageContent.length > MAX_MAILTO_LENGTH ? (messageContent.slice(0, MAX_MAILTO_LENGTH - 1) + '…') : messageContent;
        const subject = encodeURIComponent(this.#shareData.title ?? document.title ?? '');
        const body = encodeURIComponent(message);
        const url = `mailto:?subject=${subject}&body=${body}`;

        this.#openUrl(url);
        this.#closeDialog();
    }

    /**
     * Handles SMS target.
     * 
     * @private
     * @memberof ShareButton
     */
    #handleSmsTarget() {
        const MAX_SMS_LENGTH = 918;
        const messageContent = this.#buildShareContent('\n');
        const message = messageContent.length > MAX_SMS_LENGTH ? (messageContent.slice(0, MAX_SMS_LENGTH - 1) + '…') : messageContent;
        const body = encodeURIComponent(message);
        const url = this.isIos ? `sms:&body=${body}` : `sms:?body=${body}`;

        this.#openUrl(url);
        this.#closeDialog();
    }

    /**
     * Handles WhatsApp target.
     * 
     * @private
     * @memberof ShareButton
     */
    #handleWhatsAppTarget() {
        const MAX_WHATSAPP_LENGTH = 4096;
        const messageContent = this.#buildShareContent('\n');
        const message = messageContent.length > MAX_WHATSAPP_LENGTH ? (messageContent.slice(0, MAX_WHATSAPP_LENGTH - 1) + '…') : messageContent;
        const body = encodeURIComponent(message);
        const url = `whatsapp://send?text=${body}`;

        this.#openUrl(url);
        this.#closeDialog();
    }

    /**
     * Locks the dialog during share operations.
     * Disables all target buttons and shows busy state.
     * 
     * @private
     * @memberof ShareButton
     */
    #lockDialog() {
        if (!this.#dialog) {
            return;
        }

        this.#isSharing = true;
        application.lockScreen(true, null, coreUI.isMobile ? window.top.body : null);
    }

    /**
     * Opens the share dialog.
     * @returns {Promise<void>}
     * 
     * @private
     * @memberof ShareButton
     */
    async #openDialog() {
        if (!this.#dialog) {
            this.#createDialog();
        }

        this.#abortController = new AbortController();
        this.#previouslyFocused = document.activeElement;

        const dialogContent = this.#renderDialogContent();

        if (this.#dialog.tagName.toLowerCase() === 'dialog-window') {
            requestAnimationFrame(() => {
                this.#dialog.open(dialogContent, {
                    autoSize: true,
                    title: this.#locales?.share || 'Delen'
                });
                
                dialogContent.querySelector('.share-target-button')?.focus();
                this.#isSharing = false;
            });
        }
        else {
            this.#dialog.appendChild(dialogContent);
            this.#dialog.showModal();
            
            dialogContent.querySelector('.share-target-button')?.focus();
            this.#isSharing = false;
        }
    }

    /**
     * Opens a URL using the appropriate method for the platform.
     * @param {string} url URL to open.
     * 
     * @private
     * @memberof ShareButton
     */
    #openUrl(url) {
        if (coreUI.isMobile()) {
            // On iPhone/mobile, we need to use different approaches for better reliability.
            if (this.isIos) {
                // For iOS in iframe context, try to open URL in parent window.
                // URL schemes often don't work properly from within iframes on iOS.
                const targetWindow = window !== window.top ? window.top : window;
                
                try {
                    // Create link in the target window context.
                    const link = targetWindow.document.createElement('a');
                    link.href = url;
                    link.style.position = 'fixed';
                    link.style.opacity = '0';
                    link.style.pointerEvents = 'none';
                    
                    targetWindow.document.body.appendChild(link);
                    link.click();
                    targetWindow.document.body.removeChild(link);
                }
                catch (error) {
                    // Fallback if cross-origin or other issues.
                    const link = document.createElement('a');
                    link.href = url;
                    link.style.position = 'fixed';
                    link.style.opacity = '0';
                    link.style.pointerEvents = 'none';
                    
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            }
            else {
                // For other mobile platforms, use location.href.
                window.location.href = url;
            }
        }
        else {
            const link = document.createElement('a');
            link.href = url;
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    /**
     * Renders the button markup.
     * 
     * @private
     * @memberof ShareButton
     */
    #render() {
        const buttonClass = (this.getAttribute('class') || '').trim();
        const iconClassname = this.getAttribute('icon-classname') || '';
        const label = this.getAttribute('label');

        this.#button = document.createElement('button');
        this.#button.type = 'button';

        if (buttonClass) {
            this.#button.className = buttonClass;
            this.removeAttribute('class');
        }

        const iconSpan = document.createElement('span');
        iconSpan.className = `button-icon ${iconClassname}`.trim();
        iconSpan.setAttribute('aria-hidden', 'true');

        const labelSpan = document.createElement('span');
        labelSpan.className = 'button-label';
        labelSpan.textContent = label ?? '';

        // Ensure button always has accessible name.
        this.#setButtonAriaLabel();

        this.#button.appendChild(iconSpan);
        this.#button.appendChild(labelSpan);

        this.appendChild(this.#button);
        this.#updateDisabledState();
    }

    /**
     * Renders the dialog content.
     * @returns {HTMLElement} The dialog content element.
     * 
     * @private
     * @memberof ShareButton
     */
    #renderDialogContent() {
        const shareLabel = this.#locales?.share || 'Delen';
        const doc = this.#dialog?.ownerDocument || document;
        const contentContainer = doc.createElement('div');

        contentContainer.className = 'share-dialog-content';

        const targetGrid = doc.createElement('div');
        targetGrid.className = 'share-dialog-targets';
        targetGrid.setAttribute('role', 'group');
        
        if (shareLabel) {
            targetGrid.setAttribute('aria-label', shareLabel);
        }

        const targetsToRender = this.#availableTargets || this.#getAvailableTargets();

        targetsToRender.forEach((target) => {
            const button = this.#createTargetButton(target);

            targetGrid.appendChild(button);
        });

        contentContainer.appendChild(targetGrid);

        return contentContainer;
    }

    /**
     * Sets the button's ARIA label with proper fallbacks.
     * 
     * @private
     * @memberof ShareButton
     */
    #setButtonAriaLabel() {
        if (!this.#button) {
            return;
        }

        const explicit = this.getAttribute('label') || this.getAttribute('title') || this.getAttribute('aria-label');
        const label = explicit || (this.#locales?.share || 'Delen');

        if (label) {
            this.#button.setAttribute('aria-label', label);
        }
    }

    /**
     * Updates button content (label and icon classes).
     * 
     * @private
     * @memberof ShareButton
     */
    #updateButtonContent() {
        if (!this.#button) {
            return;
        }

        const buttonClass = this.getAttribute('class');
        const iconClassname = this.getAttribute('icon-classname') || '';
        const label = this.getAttribute('label');
        const iconSpan = this.#button.querySelector('.button-icon');
        const labelSpan = this.#button.querySelector('.button-label');

        if (iconSpan) {
            iconSpan.className = `button-icon ${iconClassname}`.trim();
            iconSpan.setAttribute('aria-hidden', 'true');
        }

        if (labelSpan) {
            labelSpan.textContent = label ?? '';
        }

        this.#setButtonAriaLabel();

        if (buttonClass !== null) {
            const normalized = buttonClass.trim();

            this.#button.className = normalized;
            this.removeAttribute('class');

            return;
        }
    }

    /**
     * Updates the disabled state of the button.
     * 
     * @private
     * @memberof ShareButton
     */
    #updateDisabledState() {
        if (!this.#button) {
            return;
        }

        const isDisabled = this.hasAttribute('disabled');

        this.#button.disabled = isDisabled;

        if (isDisabled) {
            this.#button.setAttribute('aria-disabled', 'true');
        }
        else {
            this.#button.removeAttribute('aria-disabled');
        }
    }

    /**
     * Unlocks the dialog after share operations complete.
     * Re-enables all target buttons and removes busy state.
     * 
     * @private
     * @memberof ShareButton
     */
    #unlockDialog() {
        if (!this.#dialog) {
            return;
        }

        this.#isSharing = false;
        application.lockScreen(false);
    }
    // #endregion

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'share-button' and maps it to the ShareButton class.
 */
customElements.define(ShareButton.tagName, ShareButton);
